import React, { useRef } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';
import styled from 'styled-components';

import type { IScrollable, IConditionalScrollable } from './types';

export const ConditionalScrollable = (props: IConditionalScrollable): JSX.Element | null => {
  const { offset = 0, applyScroll = true, children } = props;

  if (!applyScroll) return <>{children}</>;

  return <Scrollable offset={offset}>{children}</Scrollable>;
};

export const Scrollable = (props: IScrollable): JSX.Element | null => {
  const { offset = 0, children } = props;

  const leftRef = useRef<HTMLDivElement | null>(null);
  const leftEntry = useIntersectionObserver(leftRef, {});
  const isVisibleLeft = !!leftEntry?.isIntersecting;

  const rightRef = useRef<HTMLDivElement | null>(null);
  const rightEntry = useIntersectionObserver(rightRef, {});
  const isVisibleRight = !!rightEntry?.isIntersecting;

  return (
    <ShadowsContainer $leftShadow={!isVisibleLeft} $rightShadow={!isVisibleRight} $offset={offset}>
      <ScrollContainer>
        <div ref={leftRef} />
        {children}
        <Spacer ref={rightRef} />
      </ScrollContainer>
    </ShadowsContainer>
  );
};

const ScrollContainer = styled.div`
  overflow-x: auto;
  width: 100%;

  position: relative;
  display: flex;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    height: 0px;
    background: transparent; /* Chrome/Safari/Webkit */
  }
`;

const ShadowsContainer = styled.div<{
  $leftShadow: boolean;
  $rightShadow: boolean;
  $offset: number;
}>`
  position: relative;
  display: inline-block;

  ${({ $offset }) => `width: calc(100vw - ${$offset + 8}px);`}

  ${({ $leftShadow }) =>
    $leftShadow &&
    `
    &:before {
      content: '';
      position: absolute;
      top: 0;
      width: 40px;
      height: 100%;
      z-index: 1;

      left: 0;
      background: linear-gradient(-90deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 100%);
    }
  `}

  ${({ $rightShadow }) =>
    $rightShadow &&
    `
    &:after {
      content: '';
      position: absolute;
      top: 0;
      width: 40px;
      height: 100%;
      z-index: 1;

      right: 0;
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
    }
  `}
`;

const Spacer = styled.div`
  min-size: 1px;
`;
